import { useEffect, useState } from 'react';

const CONFETTI_COLORS = ['#FF6B35', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6'];
const CONFETTI_PARTICLES = Array.from({ length: 20 }, () => ({
  left: `${20 + Math.random() * 60}%`,
  backgroundColor: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
  animationDelay: `${Math.random() * 0.5}s`,
  animationDuration: `${1 + Math.random() * 1}s`,
}));

interface SuccessAnimationProps {
  onComplete?: () => void;
}

/**
 * Orchestrated success celebration with staggered entrance timing:
 *   100ms: checkmark circle scales in
 *   300ms: confetti particles begin falling
 *   500ms: "Booking Added!" text fades up
 *   2500ms: onComplete fires (typically closes the modal)
 *
 * Each phase uses independent state flags rather than a single step
 * counter so animations can overlap naturally.
 */
export function SuccessAnimation({ onComplete }: SuccessAnimationProps) {
  const [showCheckmark, setShowCheckmark] = useState(false);
  const [showText, setShowText] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Stagger the animations
    const timer1 = setTimeout(() => setShowCheckmark(true), 100);
    const timer2 = setTimeout(() => setShowConfetti(true), 300);
    const timer3 = setTimeout(() => setShowText(true), 500);
    const timer4 = setTimeout(() => {
      if (onComplete) onComplete();
    }, 2500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [onComplete]);

  return (
    <div className="flex flex-col items-center justify-center py-12 relative overflow-hidden">
      {/* Confetti particles */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {CONFETTI_PARTICLES.map((particle, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-confetti"
              style={{ top: '-10px', ...particle }}
            />
          ))}
        </div>
      )}

      {/* Success circle */}
      <div className={`
        relative w-24 h-24 rounded-full bg-gradient-to-br from-savings to-emerald-400
        flex items-center justify-center shadow-lg shadow-savings/30
        transition-[transform,opacity] duration-500 ease-out
        ${showCheckmark ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}
      `}>
        {/* Pulse rings */}
        <div className="absolute inset-0 rounded-full bg-savings/20 animate-ping" />
        <div className="absolute inset-0 rounded-full bg-savings/10 animate-ping" style={{ animationDelay: '0.2s' }} />

        {/* Checkmark */}
        <svg
          className={`w-12 h-12 text-white transition-[transform,opacity] duration-300 ${showCheckmark ? 'scale-100' : 'scale-0'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{ transitionDelay: '200ms' }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 13l4 4L19 7"
            className="animate-draw-check"
          />
        </svg>
      </div>

      {/* Success text */}
      <div className={`
        mt-6 text-center transition-[transform,opacity] duration-500
        ${showText ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
      `}>
        <h3 className="text-xl font-bold text-stone-900 dark:text-stone-100 mb-2">
          Booking Added!
        </h3>
        <p className="text-stone-600 dark:text-stone-400 text-sm">
          We're now watching this flight.
        </p>
      </div>

      {/* Plane flying across */}
      <div className={`
        absolute transition-[transform,opacity] duration-1000 ease-out
        ${showConfetti ? 'translate-x-[200%] opacity-0' : '-translate-x-[200%] opacity-100'}
      `}
      style={{ top: '30%', transitionDelay: '200ms' }}
      >
        <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
        </svg>
      </div>
    </div>
  );
}
